import React, { useState } from 'react';

import { CANNED_FIELD_METADATA } from '@aline/common';
import { params } from '@aline/routes';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { AlineAvatar, AlineTooltip, Spacer } from 'src/components';
import { CompletedIcon } from 'src/components/icons/CompletedIcon';
import { HorizontalDotsIcon } from 'src/components/icons/HorizontalDotsIcon';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import type { Apollo } from 'src/gql/generated';
import { getRecipientAssigneeDisplayDetails, getRecipientAvatarInfo } from 'src/helpers';
import {
  useDocumentSignatureStatus,
  useIsAuthorizedToEditStorable,
  useRemoveCannedFieldFromDocument,
} from 'src/hooks';
import { useMaybeEditor } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  completedIcon: {
    color: theme.appColors.accent2,
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.25),
    width: '100%',
  },
  icon: {
    height: 18,
    width: 18,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

interface CannedFieldRowHeaderProps {
  cannedField: Apollo.CannedFieldFullFragment;
  documentId: UUID;
  isCompleted: boolean;
}

export function CannedFieldRowHeader({
  cannedField,
  documentId,
  isCompleted,
}: CannedFieldRowHeaderProps): JSX.Element {
  const { classes } = useStyles();
  const editor = useMaybeEditor();
  const documentViewer = useMaybeDocumentViewer();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const { isSigned, wasSentForSignature } = useDocumentSignatureStatus(documentId);
  const { setValue: setEditFieldId } = useDrawerSearchParam(params.rightPanel.editFieldId);
  const { removeCannedFieldFromDocument } = useRemoveCannedFieldFromDocument();
  const meta = CANNED_FIELD_METADATA[cannedField.variant];
  const { avatarColor, primary: tooltip } = getRecipientAssigneeDisplayDetails(
    cannedField.assignee
  );
  const isDeleteDisabled = !canEdit || wasSentForSignature || isSigned;

  function handleEdit(e: React.MouseEvent): void {
    e.stopPropagation();
    setOpen(false);
    setEditFieldId(cannedField.id);
  }

  async function handleDelete(e: React.MouseEvent): Promise<void> {
    e.stopPropagation();
    setOpen(false);
    await removeCannedFieldFromDocument({
      cannedField: { id: cannedField.id, nodeId: cannedField.nodeId },
      documentId,
      documentViewer,
      editor,
      tabId,
      workflowId: cannedField.workflowId,
    });
  }

  return (
    <div className={classes.header}>
      <Typography noWrap className={classes.label}>
        {meta.label}
      </Typography>
      <Spacer />
      {isCompleted && <CompletedIcon className={classes.completedIcon} />}
      <AlineTooltip arrow placement="top" title={tooltip}>
        <div>
          <AlineAvatar
            avatarInfo={getRecipientAvatarInfo(cannedField.assignee)}
            styles={{ backgroundColor: avatarColor }}
          />
        </div>
      </AlineTooltip>
      <IconButton
        ref={setAnchorEl}
        size="small"
        onClick={e => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <HorizontalDotsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <MenuItem onClick={handleEdit}>
          <Typography variant="body1">Edit</Typography>
        </MenuItem>
        <MenuItem disabled={isDeleteDisabled} onClick={handleDelete}>
          <Typography variant="body1">Delete</Typography>
        </MenuItem>
      </Menu>
    </div>
  );
}
